import { CoreApplicationContext } from '@eventbrite/context-gen';
import { RootErrorFallback } from '@eventbrite/eds-structure';
import { FeatureFlags } from '@eventbrite/feature-flag-utils';
import {
    AuthenticatedHeader,
    CamelCaseLocation,
    RenderGlobalNavOrUndefined,
} from '@eventbrite/global-nav';
import { gettext } from '@eventbrite/i18n';
import { SiteStructure } from '@eventbrite/site-structure';
import * as Sentry from '@sentry/react';
import React from 'react';
import { FooterLinks, RequestShape, UserShape } from '../types';

interface Breadcrumb {
    title: string;
    relative_url: string;
    absolute_url: string;
}

interface ThingsToDoBasePageProps {
    env: CoreApplicationContext['env'];
    footerLinks: FooterLinks;
    request: RequestShape;
    user: UserShape;
    children?: React.ReactNode;
    featureFlags: FeatureFlags;
    breadcrumbs?: Breadcrumb[];
    currentPlace?: string;
    location?: CamelCaseLocation;
}

export const ThingsToDoBasePage: React.FunctionComponent<
    ThingsToDoBasePageProps
> = (props) => {
    const { env, request, user, featureFlags, breadcrumbs, location } = props;

    const globalHeader = user.isAuthenticated ? (
        <AuthenticatedHeader
            env={env}
            request={request}
            user={user}
            hasBreadcrumbs={breadcrumbs && breadcrumbs.length > 0}
            featureFlags={featureFlags}
            action_from={'ttd'}
            location={location}
        />
    ) : (
        RenderGlobalNavOrUndefined({
            config: {
                env,
                user,
                searchBar: {
                    renderSearchBar: true,
                    desktopLabel: gettext('Search events'),
                    mobileLabel: gettext('Search'),
                    location,
                },
            },
            featureFlag: featureFlags?.enableIaGlobalNav ?? false,
        })
    );

    return (
        <SiteStructure
            env={env}
            request={request}
            user={user}
            footerLinks={props.footerLinks}
            fullScreenMain={true}
            globalHeader={globalHeader}
        >
            <Sentry.ErrorBoundary
                fallback={<RootErrorFallback />}
                onError={(error) => {
                    if (process.env.NODE_ENV !== 'production') {
                        throw error;
                    }
                }}
            >
                {props.children}
            </Sentry.ErrorBoundary>
        </SiteStructure>
    );
};
